import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, startWith } from 'rxjs';
import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeOfficerType } from '@models/relationships/company-change-officer-type';
import { RelationshipType, RelationshipTypeLabels } from '@models/relationships/relationship-type.enum';
import { OfficerAppointmentType } from '@models/relationships/officer-appointment-type';
import { OfficerRelationshipDetails, Relationship } from '@models/relationships/relationship';
import { SelectOption } from '@models/ui-models/select-options';
import {
  CompanyChangeOfficer,
  ReasonOfCessation,
  reasonOfCessationOptions
} from '../../../models/company/company-change-officer.model';
import { ACNPipe } from '../../../../shared/pipes/acnPipe';
import { DatePickerComponent } from '@shared/controls/date-picker/date-picker.component';
import { SelectComponent } from '@shared/controls/select/select.component';
import { TextareaComponent } from '@shared/controls/textarea/textarea.component';
import { NotificationComponent } from "@shared/components/commons/notification/notification.component";
import { CheckboxGroupComponent } from "@shared/controls/checkbox-group/checkbox-group.component";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";

export enum CeaseCompanyOfficeholderStepsEnum {
  FormDescription = 0,
  CeaseCompanyOfficeholder = 1
}

@Component({
  selector: 'app-company-change-officer-cease',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePickerComponent,
    SelectComponent,
    CheckboxGroupComponent,
    NotificationComponent,
    TextareaComponent,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    DisclaimerComponent,
  ],
  templateUrl: './company-change-officer-cease.component.html',
  styleUrls: [
    './company-change-officer-cease.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
  ]
})
export class CompanyChangeOfficerCeaseComponent extends BaseStepperFormComponent<CeaseCompanyOfficeholderStepsEnum, CompanyChangeOfficer> implements OnInit {
  cdr = inject(ChangeDetectorRef);

  @Input() officers: Record<string, Relationship[]> = {};
  @Input() officersSelectOptions: SelectOption[] = [];
  @Input() moreThenOneDirectorExist: boolean | undefined;
  @Input() allDirectorsOptions: SelectOption[] = [];

  override readonly StepsEnum = CeaseCompanyOfficeholderStepsEnum;
  readonly reasonOfCessationOptions = reasonOfCessationOptions;
  readonly ceaseWarningMsg = `You cannot cease the sole director of the company. Please appoint a new director before proceeding with this cessation.`;

  officerPositionTypesOptions: SelectOption[] = [];
  alternativeDirectorsForOptions: SelectOption<string>[] = [];
  isAlternativeDirectorSelected = false;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;

  form = new FormGroup({
    changeDate: new FormControl<Date | null>(null),
    appointedOfficerId: new FormControl<string | null>(null, [Validators.required]),
    types: new FormControl<RelationshipType[]>([]),
    cessationReason: new FormControl<ReasonOfCessation | null>(null, [Validators.required]),
    noticeOfResignation: new FormControl<string | null>(null, [Validators.required]),
    moreThanOneDirector: new FormControl<boolean | null>(null, [Validators.requiredTrue]),

    alternativeDirectorFor: new FormControl<string | null>(null, [Validators.required]),
  });

  override stepperForm = new FormGroup({
    [CeaseCompanyOfficeholderStepsEnum.FormDescription]: new FormGroup({}),
    [CeaseCompanyOfficeholderStepsEnum.CeaseCompanyOfficeholder]: this.form
  });

  constructor() {
    super();
    this.setupSteps(CeaseCompanyOfficeholderStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDateOfEstablishmentMinDate(this.companyChangeData.dateOfEstablishment);
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    this.form.controls.changeDate.setValue(DatepickerHelper.getToday());
    this.form.controls.moreThanOneDirector.setValue(true);

    this.setTypesValidators();

    this.listenReasonOfCessationChange();
    this.listenOfficerTypesChange();
    this.listenAppointedOfficerIdChanges();
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);

    this.setupChange();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeOfficer);
  }

  override setupChange(change: CompanyChangeOfficer = this.formModel) {
    if (change.offices.length && change.offices[0].relationshipId) {
      const selectedRelationshipIds = change.offices[0]?.relationshipId;
      const selectedOfficerId = Object.entries(this.officers)
        .find(([key, relashionships]) => relashionships
          .some(relashionship => relashionship.relationshipId === selectedRelationshipIds)
        )?.[0] ?? null; // it's a key
      this.form.controls.appointedOfficerId.patchValue(selectedOfficerId ?? null);
      this.cdr.detectChanges();
      this.form.controls.types.setValue(change.offices.map((relationship) => relationship.type));

      const selectedAlternativeOfficer = change.offices.find((selectedOfficer) => selectedOfficer.type == RelationshipType.AlternativeDirector);
      this.selectAlternativeDirectorForByRelationship(selectedAlternativeOfficer);
    }

    if (!this.isEdit && change === this.formModel)
      return;

    const selectedChangeOfficer = change.offices[0];
    this.form.patchValue({
      ...change,
      changeDate: change.changeDate,
      appointedOfficerId: selectedChangeOfficer.key,
    });
    this.cdr.detectChanges();
    this.form.controls.cessationReason.setValue(change.cessationReason);
    this.form.controls.noticeOfResignation.setValue(change.noticeOfResignation);
    this.cdr.detectChanges();

    const types = change.offices.map((relationship) => relationship.type);
    this.form.controls.types.setValue(types);
  }

  override buildDocument(): Document | null {
    const selectedOfficerRelationships = this.officers[this.form.value.appointedOfficerId as string];
    const offices = selectedOfficerRelationships
      .filter((relationship) => this.form.controls.types.value?.includes(relationship.type))
      .map((relationship) => {
        if (relationship.type == RelationshipType.AlternativeDirector && this.form.controls.alternativeDirectorFor.value) {
          const relatedDirector = this.officers[this.form.controls.alternativeDirectorFor.value].find((officer) => officer.type == RelationshipType.Director);
          const copy = new Relationship(relationship);
          copy.details = new OfficerRelationshipDetails({
            ...relationship.details,
            alternativeDirectorFor: relatedDirector?.relationshipId
          });

          return copy;
        }

        return relationship;
      });

    const changes = new CompanyChangeOfficer({
      ...this.form.value,
      actionType: CompanyChangeOfficerType.Removal,
      changeDate: this.form.controls.changeDate.value!,
      offices,
    });

    changes.description = changes.offices[0].individualDataOverride?.fullNameLabel
      ? 'Cessation of ' + changes.offices[0].individualDataOverride?.fullNameLabel
      : '';

    try {
      return new Document({
        changes: [changes],
        entityId: this.companyChangeData.entityId,
        type: 'c:484',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  private listenAppointedOfficerIdChanges(): void {
    this.form.controls.appointedOfficerId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((appointedOfficerId) => {
        if (appointedOfficerId) {
          const relatedRelationships = this.officers[appointedOfficerId];
          this.officerPositionTypesOptions = relatedRelationships.map((officer, relationshipIndex) => {

            if (officer.type === RelationshipType.AlternativeDirector && officer.details instanceof OfficerRelationshipDetails && officer.details.alternativeDirectorFor) {
              return {
                label: `${ RelationshipTypeLabels[officer.type][officer.type] } (for ${ this.officers[officer.details.alternativeDirectorFor][0].individualDataOverride!.fullNameLabel })`,
                value: officer.type
              };
            }

            return {
              label: RelationshipTypeLabels[officer.type],
              value: officer.type
            };
          });

          this.alternativeDirectorsForOptions = this.allDirectorsOptions.filter((director) => director.value != appointedOfficerId) as SelectOption<string>[];
        } else {
          this.alternativeDirectorsForOptions = [];
          this.officerPositionTypesOptions = [];
          this.form.controls.cessationReason.reset();
        }

        this.form.controls.types.setValue([]);
      });
  }

  onSelectAppointedOfficer(option: SelectOption | null): void {
    const appointedOfficerId = option?.value as string | null;

    if (!appointedOfficerId) return;

    const relatedRelationships = this.officers[appointedOfficerId];
    const alternativeDirectorRelatedRelationship = relatedRelationships.find((relationship) => relationship.type == RelationshipType.AlternativeDirector);
    this.selectAlternativeDirectorForByRelationship(alternativeDirectorRelatedRelationship);
  }

  private listenOfficerTypesChange(): void {
    this.form.controls.types.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((types) => {
        const isDirectorSelected = this.selectedOfficerPositions.includes(OfficerAppointmentType.Director);
        const isNonDirectorSelected = this.selectedOfficerPositions.some(position => position !== OfficerAppointmentType.Director);

        if (isNonDirectorSelected) {
          this.form.controls.moreThanOneDirector.setValue(true);
        } else if (isDirectorSelected) {
          this.form.controls.moreThanOneDirector.setValue(!!this.moreThenOneDirectorExist);
        }

        if (types !== null) {
          this.officerPositionTypesOptions.forEach((option) => {
            if (option.label === 'Director') {
              const isSelected = types.includes(option.value as RelationshipType);
              this.form.controls.moreThanOneDirector.setValue(isSelected ? !!this.moreThenOneDirectorExist : true);
            }
          });
        }

        this.isAlternativeDirectorSelected = types?.includes(RelationshipType.AlternativeDirector) ?? false;
        setControlDisabled(this.form.controls.alternativeDirectorFor, !this.isAlternativeDirectorSelected);
      });

    combineLatest([
      this.form.controls.types.valueChanges,
      this.form.controls.changeDate.valueChanges.pipe(startWith(this.form.controls.changeDate.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fillNoticeOfResignation());
  }

  private listenReasonOfCessationChange(): void {
    this.form.controls.cessationReason.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reasonOfCessation) => {
        if (!this.form.controls.appointedOfficerId.value) return;

        setControlDisabled(this.form.controls.noticeOfResignation, reasonOfCessation !== ReasonOfCessation.Resignation);
        setControlDisabled(this.form.controls.types, reasonOfCessation === ReasonOfCessation.Death);

        if (reasonOfCessation === ReasonOfCessation.Death) {
          const officerTypes = this.officers[this.form.controls.appointedOfficerId.value!].map((officer) => officer.type);
          this.form.controls.types.setValue(officerTypes);
        }

        if (reasonOfCessation === ReasonOfCessation.Resignation) {
          this.fillNoticeOfResignation();
        }
      });
  }

  private setTypesValidators(): void {
    CheckboxGroupComponent.addRequiredAtLeastOneValidator(this.form.controls.types);
  }

  private fillNoticeOfResignation(): void {
    const allowedToFillNoticeOfResignation =
      this.form.controls.cessationReason.value === ReasonOfCessation.Resignation
      && this.form.controls.appointedOfficerId.value !== null
      && this.form.controls.cessationReason.value !== null
      && this.form.controls.changeDate.value !== null;

    if (!allowedToFillNoticeOfResignation)
      return;

    const officer = this.officers[this.form.controls.appointedOfficerId.value as string];
    const officerName = officer?.[0]?.individualDataOverride?.fullNameLabel ?? '';
    const officerAddress = officer?.[0]?.individualDataOverride?.address.normalizedFullAddress ?? '';
    const positions = this.selectedOfficerPositions.length ? ' as ' + this.selectedOfficerPositions.map(type => RelationshipTypeLabels[type]).join(', ') : '';
    const nameOfCompanyAndACN = this.companyChangeData.companyName + (this.companyChangeData.companyAcn ? ` - ${ new ACNPipe().transform(this.companyChangeData.companyAcn) }` : '');
    const resignationDate = DatepickerHelper.getAuLongDate(this.form.controls.changeDate.value as Date);

    // 'Notice of Resignation' text field should appear with pre-filled text:
    // 'I, [Officer name] of [Officer Address] hereby resign as [role (Director; Secretary; Director and Secretary)]
    // of [Company name - ACN] with effect from [Resignation Date]'
    const noticeOfResignation = `I, ${ officerName } of ${ officerAddress.trim() } hereby resign${ positions } of ${ nameOfCompanyAndACN } with effect from ${ resignationDate }.`;
    this.form.controls.noticeOfResignation.setValue(noticeOfResignation);
  }

  selectAlternativeDirectorForByRelationship(relationship: Relationship | undefined | null): void {
    if (!relationship || relationship.type != RelationshipType.AlternativeDirector) return;

    if (relationship.details?.$type == 'OfficerRelationshipDetails' && relationship.individualId) {
      const details = relationship.details as OfficerRelationshipDetails;
      const allOfficers = Object.values(this.officers).flat();
      const alternativeDirectorFor = allOfficers.find((officer) => officer.relationshipId == details.alternativeDirectorFor);

      this.form.controls.alternativeDirectorFor.setValue(alternativeDirectorFor?.key || null);
    }
  }

  get selectedOfficerPositions(): OfficerAppointmentType[] {
    return this.officers[this.form.controls.appointedOfficerId.value as string]
      ?.filter((officer) => this.form.controls.types.value?.includes(officer.type))
      ?.map(officer => officer.type) as any as OfficerAppointmentType[] ?? [];
  }

  get noticeOfResignationHidden(): boolean {
    return this.form.controls.cessationReason.value !== ReasonOfCessation.Resignation;
  }
}
